import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { AdminSearch } from 'app/components/AdminSearchField';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { BlurredLabel, PageMainLabel } from 'app/components/SHLabel';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import RidersTable from './RidersTable';
import useRiderApi from 'admin/hooks/useRiderApi';
import { useDebounce } from 'hooks/useDebounce';
import useCommonLookUpDatas from 'hooks/useCommonLookupData';
import { getLookupArray, useNavigateToRoute } from 'utils/common';
import AdminMultiSelectDropDown from 'admin/components/AdminMultiSelectDropdown';
import { RiderFilteredData } from 'admin/components/AdminMultiSelectDropdown/FilteredList';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminDropDown from 'app/components/AdminDropDown';
import { GeneralLookup } from 'services/openapi';
import React from 'react';
import { CommonLoader } from 'app/components/CommonLoader';
import { usePermission } from 'hooks/usePermission';
import Cookies from 'js-cookie';

const RidersPage = () => {
  const userType = Cookies.get('userType') ?? '';
  const { status } = useParams();
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const { hasViewPermission } = usePermission();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const breadCrumbItems = [
    {
      id: '',
      text: userType === 'COACH' ? 'Showyeda coach' : 'Showyeda admin',
      disabled: true,
    },
    {
      id: '',
      text: 'Riders',
      disabled: true,
    },
  ];

  const {
    fetchActiveRiderList,
    activeRiderList,
    deleteRiderInfo,
    fetchRiderActiveOrInactive,
    handleDownloadRiderPointReport,
    loading,
    downloadLoading,
  } = useRiderApi();
  const { getAllLookupData, lookUps } = useCommonLookUpDatas();
  const redirect = useNavigateToRoute();
  const [paidFilter, setPaidFilter] = useState<number | null>(null);

  // Initialize selectedItemId based on URL status parameter
  const getInitialSelectedItemId = () => {
    if (status === 'active') {
      return 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75';
    } else if (status === 'inactive') {
      return 'f7f4cf58-a6fd-4646-9e25-d9517f92709e';
    }
    // No status param means show both (no filter)
    return null;
  };

  const [selectedItemId, setSelectedItemId] = useState<string | null>(getInitialSelectedItemId());
  const [active, setActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageSkip, setPageSkip] = useState<number | undefined>(0);
  const [lookupData, setLookupData] = useState('');
  const [teamIds, setTeamIds] = useState('');
  const [divisionIds, setDivisionIds] = useState('');
  const [categoryIds, setCategoryIds] = useState('');
  const debounceRiderSearch = useDebounce(searchTerm);
  const [preDataList, setPreDataList] =
    useState<{ name: string; id: string; type: string }[]>();

  const handleItemIdClick = (itemId: string) => {
    const newSelectedItemId = itemId === selectedItemId ? null : itemId;
    setSelectedItemId(newSelectedItemId);

    const basePath = userType === 'COACH' ? '/coach' : '/admin';

    if (newSelectedItemId) {
      // A filter is selected
      let statusRoute = '';
      if (itemId === 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75') {
        statusRoute = 'active';
      } else if (itemId === 'f7f4cf58-a6fd-4646-9e25-d9517f92709e') {
        statusRoute = 'inactive';
      }

      if (statusRoute) {
        const fullPath = isPastDataFlag
          ? `${basePath}/past-data/riders/${statusRoute}`
          : `${basePath}/riders/${statusRoute}`;
        navigate(fullPath, { replace: true });
      }
    } else {
      // No filter selected (deselected) - navigate to base path (shows both)
      const fullPath = isPastDataFlag
        ? `${basePath}/past-data/riders`
        : `${basePath}/riders`;
      navigate(fullPath, { replace: true });
    }

    fetchActiveRiderList(
      '',
      teamId,
      divisionId,
      categoryIdsArray,
      debounceRiderSearch,
      newSelectedItemId ?? '',
      sortField,
      0,
      0,
      pageSize,
      paidFilter as number,
    );
  };

  const isTeam = preDataList?.find(item => item.type === 'TEAM');
  const isDivision = preDataList?.find(item => item.type === 'DIVISION');
  const teamIdsArray = teamIds ? teamIds.split(',') : [];
  const divisionIdsArray = divisionIds ? divisionIds.split(',') : [];
  const categoryIdsArray = categoryIds ? categoryIds.split(',') : [];
  const teamId = isTeam ? teamIdsArray : [];
  const divisionId = isDivision ? divisionIdsArray : [];
  const [seasonPayload, setSeasonPayload] = React.useState<GeneralLookup>({
    id: '',
    name: '',
  });
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
    id: '50',
    name: '50',
  });

  // Page size options for the dropdown
  const pageSizeOptions: GeneralLookup[] = [
    { id: '25', name: '25' },
    { id: '50', name: '50' },
    { id: '100', name: '100' },
    { id: '200', name: '200' },
  ];

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleDropdownChange = (data: GeneralLookup) => {
    setSeasonPayload(data);
    fetchActiveRiderList(
      data?.id,
      teamId,
      divisionId,
      categoryIdsArray,
      debounceRiderSearch,
      selectedItemId ?? '',
      sortField,
      0,
      0,
      pageSize,
      paidFilter as number,
    );
  };

  const handleRiderDwoload = async () => {
    try {
      const url: any = await handleDownloadRiderPointReport(
        seasonPayload?.id,
        teamId,
        divisionId,
        debounceRiderSearch,
        selectedItemId ?? '',
        sortField,
        0,
        paidFilter as number,
      );

      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!; // Use the extracted filename

          // Append link to body (required for Firefox)
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });

      return url;
    } catch (error) {
      console.error('Error downloading team point report:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (debounceRiderSearch.length > 0) {
      fetchActiveRiderList(
        '',
        teamId,
        divisionId,
        categoryIdsArray,
        debounceRiderSearch,
        selectedItemId ?? "",
        sortField,
        0,
        0,
        pageSize,
        paidFilter as number,
      );
    } else if (status) {
      // Use URL status parameter if provided
      const activeStatusId =
        status === 'active'
          ? 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75'
          : status === 'inactive'
            ? 'f7f4cf58-a6fd-4646-9e25-d9517f92709e'
            : null; // No status or other value means no filter (show both)
      setSelectedItemId(activeStatusId);
      fetchActiveRiderList(
        '',
        teamId,
        divisionId,
        categoryIdsArray,
        debounceRiderSearch,
        activeStatusId ?? '',
        sortField,
        0,
        0,
        pageSize,
        paidFilter as number,
      );
    } else {
      // No status param - fetch with current selectedItemId (which should be null = both)
      fetchActiveRiderList(
        '',
        teamId,
        divisionId,
        categoryIdsArray,
        debounceRiderSearch,
        selectedItemId ?? "",
        sortField,
        0,
        0,
        pageSize,
        paidFilter as number,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceRiderSearch, status]);

  // Effect to trigger fetch when filter IDs change
  useEffect(() => {
    setPageSkip(0);
    fetchActiveRiderList(
      '',
      teamId,
      divisionId,
      categoryIdsArray,
      debounceRiderSearch,
      selectedItemId ?? '',
      sortField,
      0,
      0,
      pageSize,
      paidFilter as number,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIds, divisionIds, categoryIds]);

  useEffect(() => {
    if (pageSkip !== 0) {
      if (
        debounceRiderSearch?.length! >= 0 ||
        selectedItemId ||
        teamId.length === 0 ||
        divisionId.length === 0
      ) {
        setPageSkip(0);
      }
    }
  }, [debounceRiderSearch, selectedItemId, divisionIds, teamIds]);

  useEffect(() => {
    getAllLookupData(`ACTIVESTATUS,DIVISION,CATEGORY,TEAM,SEASON`);
  }, []);

  useEffect(() => {
    if (status) {
      const activeStatusId =
        status === 'active'
          ? 'bc1e49f9-0f4a-4222-ba22-c4f4af0b4f75'
          : status === 'inactive'
            ? 'f7f4cf58-a6fd-4646-9e25-d9517f92709e'
            : null;
      setSelectedItemId(activeStatusId);
    }
  }, [status]);
  return (
    <>
      <CommonPageWrapper>
        {downloadLoading && <CommonLoader loaderType="main" />}
        <HeadingWrapper>
          <PageMainLabel>Riders</PageMainLabel>
          <CustomBreadCrumb preData={breadCrumbItems} />
        </HeadingWrapper>
        <HeadingWrapper>
          <ButtonWrapper>
            {hasViewPermission('0300101002') && (
              <>
                {!isPastDataFlag && (
                  <AdminCustomButton
                    buttonType="primary"
                    isActive={false}
                    className="fill-button"
                    onClick={() => {
                      setActive(!active);
                      redirect(
                        userType === 'COACH'
                          ? `/coach/riders/add/create`
                          : `/admin/riders/add/create`,
                      );
                    }}
                  >
                    Add Rider
                  </AdminCustomButton>
                )}
              </>
            )}

            <BlurredLabel style={{ whiteSpace: 'nowrap' }}>
              Popular filter
            </BlurredLabel>
            {getLookupArray('ACTIVESTATUS', lookUps).map(item => (
              <AdminCustomButton
                key={item.id}
                buttonType="primary"
                isActive={false}
                className={
                  selectedItemId?.includes(item?.id)
                    ? 'active-inactive'
                    : 'Inactive-button'
                }
                onClick={() => {
                  handleItemIdClick(item?.id);
                }}
              >
                {item?.name}
              </AdminCustomButton>
            ))}
            <AdminMultiSelectDropDown
              data={RiderFilteredData}
              setLookupData={setLookupData}
              filteredData={getLookupArray(lookupData, lookUps)}
              lookUps={lookUps}
              setTeamIds={setTeamIds}
              lookupData={lookupData}
              setDivisionIds={setDivisionIds}
              setCategoryIds={setCategoryIds}
              setPreDataList={setPreDataList}
              preDataList={preDataList}
            />
            {isPastDataFlag && (
              <div>
                <CustomDropDown
                  label=""
                  placeholder="Select season"
                  onDataChange={data => handleDropdownChange(data)}
                  defaultValue={seasonPayload}
                  data={getLookupArray('SEASON', lookUps)}
                  className="field-style filter-dropdown-select"
                  popupStyles="medium-dropdown"
                />
              </div>
            )}
          </ButtonWrapper>
          <div className="search-wrapper">
            <AdminCustomButton
              buttonType="primary"
              isActive={false}
              className="fill-button"
              onClick={handleRiderDwoload}
              disabled={activeRiderList?.riders?.length === 0}
            >
              Download
            </AdminCustomButton>

            <div style={{ width: '283px', paddingTop: '30px' }}>
              <AdminSearch
                placeholder="Search by name or back number"
                onSearchTextEntering={handleSearch}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </div>
        </HeadingWrapper>
        <RiderCountWrapper>
          <div>
            {(activeRiderList?.totalRidersCount ?? 0) > 10 && (
              <EntriesPerPageDropDownWrapper>
                <AdminDropDown
                  label="No: of entries per page"
                  placeholder="Select entries"
                  onDataChange={(data: GeneralLookup) => {
                    const newPageSize = parseInt(data.id, 10);
                    setPageSize(newPageSize);
                    setPageSizeSelected(data);
                    setPageSkip(0);
                    fetchActiveRiderList(
                      '',
                      teamId,
                      divisionId,
                      categoryIdsArray,
                      debounceRiderSearch,
                      selectedItemId ?? '',
                      sortField,
                      sortOrder,
                      0,
                      newPageSize,
                      paidFilter as number,
                    );
                  }}
                  defaultValue={pageSizeSelected}
                  data={pageSizeOptions}
                  className="entries-dropdown-style"
                  popupStyles="entries-dropdown-popup"
                />
              </EntriesPerPageDropDownWrapper>
            )}
          </div>
          <div className="count-style">
            <div>
              <span className="count-heading">Total Rider Count</span> : {''}
              <span className="count">{activeRiderList?.totalRidersCount}</span>
            </div>
            <div>
              <span className="count-heading">Total Try-it Rider Count</span> :{' '}
              {''}
              <span className="count">
                {activeRiderList?.totalTryItRidersCount}
              </span>
            </div>
          </div>
        </RiderCountWrapper>
        {/* Riders Table */}
        {hasViewPermission('0300101001') && (
          <RidersTable
            activeRiderList={activeRiderList}
            setPageSkip={setPageSkip}
            pageSkip={pageSkip}
            fetchActiveRiderList={fetchActiveRiderList}
            debounceRiderSearch={debounceRiderSearch}
            deleteRiderInfo={deleteRiderInfo}
            selectedItemId={selectedItemId}
            fetchRiderActiveOrInactive={fetchRiderActiveOrInactive}
            lookup={getLookupArray('ACTIVESTATUS', lookUps)}
            teamId={teamId}
            divisionId={divisionId}
            categoryId={categoryIdsArray}
            sortField={sortField}
            sortOrder={sortOrder}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            loading={loading}
            paidFilter={paidFilter}
            pageSize={pageSize}
            pageSizeSelected={pageSizeSelected}
          />
        )}
      </CommonPageWrapper>
    </>
  );
};

export default RidersPage;
const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 30px 0 0;
  align-items: center;
  .field-style {
    display: flex;
  }
`;
const RiderCountWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  padding-left: 0;
  align-items: baseline;
  justify-content: space-between;
  .count-style {
    display: flex;
    gap: 20px;
    font-family: 'Inter';
  }
  .count-heading {
    color: #8e8e8e;
    font-size: 15px;
  }
  .count {
    font-size: 13px;
    font-weight: 600;
    color: #475467;
  }
`;
const CustomDropDown = styled(AdminDropDown)`
  .k-dropdownlist {
    max-height: 31px;
    border-radius: 8px !important;
  }
  .k-input-inner {
    padding: 0 5px !important;
  }
  .k-input-value-text {
    margin: 2px 4px 2px 4px;
    font-size: 12px !important;
    letter-spacing: 0px;
    color: #1e1e1e;
    text-align: left;
  }
`;
const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  .SHdropdown {
    .label {
      margin-bottom: 8px;
      color: #475467;
    }

    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }

    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }

    .k-input-value-text {
      margin: 4px 0;
      color: #1e1e1e;
    }
  }
`;
